'use client';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  Video,
  CalendarClock,
  Check,
  History,
  ChevronRight,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

import { Card } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Checkbox } from '@/shared/ui/checkbox';
import { Button } from '@/shared/ui/button';
import { DatePicker } from '@/shared/ui/datepicker';
import { TimePicker, type TimeValue } from '@/shared/ui/timepicker';
import { cascadeContainer, cascadeItem, EASE_OUT } from '@/lib/motion';
import { meetingStamp } from '@/lib/format';
import { usePrefersReducedMotion } from '@/shared/hooks/use-prefers-reduced-motion';
import { ApiError } from '@/services/http/error-handler';
import { notify } from '@/shared/lib/notify';
import { BrandingScope, DEFAULT_BRANDING, type BrandingConfig } from '@/features/branding';
import { SLOT_MINUTES } from '@/features/meetings';
import {
  createRoomIdentitySchema,
  useCreateRoom,
  type CreateRoomIdentityValues,
  type CreatedRoom,
  type RoomMeetingKind,
} from '@/features/rooms';
import { useAuthBootstrap, isHostInjected } from '@/features/auth';
import { GateLoader } from '@/features/join';

import { MobileActionBar } from '@/shared/components/MobileActionBar';
import { ModeCards } from '../components/ModeCards';
import { CreatedResult } from '../components/CreatedResult';

type View = 'choose' | 'instant' | 'schedule' | 'created';

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Next :00/:15/:30/:45 boundary strictly after now — a sensible seed so the
 *  salesperson rarely has to spin the time wheel for a "soon" scheduled call. */
function nextQuarterHour(): TimeValue {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(Math.ceil((d.getMinutes() + 1) / 15) * 15);
  return { hours: d.getHours(), minutes: d.getMinutes() };
}

/** First-guess time when a day is chosen: soonest slot today, else 9:00 AM. */
function defaultTimeFor(day: Date): TimeValue {
  return isSameDay(day, new Date()) ? nextQuarterHour() : { hours: 9, minutes: 0 };
}

function combineDateTime(day: Date, time: TimeValue): Date {
  const d = new Date(day);
  d.setHours(time.hours, time.minutes, time.seconds ?? 0, 0);
  return d;
}

/**
 * The clean header column of a form step — icon chip + title + description, on
 * the plain card surface. No tinted hero panel, no glow: the screen reads as a
 * calm, to-the-point task, and the instant ↔ schedule switch stays one quiet,
 * continuous surface. The icon chip keeps a soft primary tint (the design
 * system's standard icon treatment), which is a small accent, not a banner.
 */
function StepHeader({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-5">
      <span className="flex h-11 w-11 items-center justify-center rounded-[var(--radius-md)] bg-[var(--primary-soft)] text-[var(--primary)]">
        {icon}
      </span>
      <div className="space-y-1.5">
        <h1 className="font-heading text-xl font-semibold tracking-tight text-[var(--text-primary)] sm:text-2xl">
          {title}
        </h1>
        <p className="text-sm leading-relaxed text-[var(--text-muted)]">{description}</p>
      </div>
      {children}
    </div>
  );
}

/**
 * Opt-in waiting-room toggle, shared by both the instant and scheduled forms
 * (Meeting.admissionRequired, defaults false — today's straight-through join
 * is unchanged unless the salesperson turns this on).
 */
function AdmissionToggle({
  checked,
  onCheckedChange,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const id = React.useId();
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface-hover)] px-4 py-3"
    >
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(state) => onCheckedChange(state === true)}
        className="mt-0.5"
      />
      <span className="space-y-0.5">
        <span className="flex items-center gap-1.5 text-sm font-medium text-[var(--text-primary)]">
          <ShieldCheck className="h-3.5 w-3.5 text-[var(--primary)]" aria-hidden="true" />
          Require host approval to join
        </span>
        <span className="block text-xs leading-relaxed text-[var(--text-muted)]">
          Guests wait in a lobby until you let them in.
        </span>
      </span>
    </label>
  );
}

/**
 * Opt-in AI sales coach + call transcript toggle, shared by both the
 * instant and scheduled forms (Room.aiCoachEnabled, defaults false).
 * Replaces the old implicit "always on for this tenant" dispatch — a
 * real behavior change from before, so this is a genuine per-meeting
 * choice now, not just a new option nobody has to think about.
 */
function AiCoachToggle({
  checked,
  onCheckedChange,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const id = React.useId();
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-start gap-3 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface-hover)] px-4 py-3"
    >
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(state) => onCheckedChange(state === true)}
        className="mt-0.5"
      />
      <span className="space-y-0.5">
        <span className="flex items-center gap-1.5 text-sm font-medium text-[var(--text-primary)]">
          <Sparkles className="h-3.5 w-3.5 text-[var(--primary)]" aria-hidden="true" />
          Enable AI sales coach
        </span>
        <span className="block text-xs leading-relaxed text-[var(--text-muted)]">
          Get live objection-handling suggestions and a call transcript. Only visible to you.
        </span>
      </span>
    </label>
  );
}

/** Small check-dot list used under the instant header — quiet reassurance. */
function StepPoints({ points }: { points: string[] }) {
  return (
    <ul className="space-y-2.5">
      {points.map((p) => (
        <li key={p} className="flex items-center gap-2.5 text-sm text-[var(--text-secondary)]">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--primary-soft)] text-[var(--primary)]">
            <Check className="h-3 w-3" aria-hidden="true" />
          </span>
          {p}
        </li>
      ))}
    </ul>
  );
}

/**
 * The shared, embeddable Creation Widget (§1, §4.1) — the ONLY meeting-creation
 * surface. Tenant-tinted via BrandingScope. Collects the two identities the
 * backend needs (the salesperson + the lead) and the schedule, then calls the
 * server-side proxy so the tenant Bearer key never touches the browser.
 *
 * One wide "modal" card at a constant width across every step, so switching
 * views never re-flows the shell — content cross-fades and the card height
 * morphs with a single Gentle spring (`layout`), which removes the width-jump
 * glitch the old max-width transition caused. Everything is built from the ui/
 * primitives (Card, Input, Button, DatePicker, TimePicker) on global tokens.
 */
export function CreationWidget({ branding = DEFAULT_BRANDING }: { branding?: BrandingConfig }) {
  const router = useRouter();
  const createRoom = useCreateRoom();

  // SSO gate (MIHIR-RESPONSE-web-token-mechanism.md) — a complete no-op
  // (auth.enabled === false) until NEXT_PUBLIC_SSO_ENABLED is flipped on,
  // which only happens once Meet is actually live on a real
  // `.app.growithnsi.com` host. Bounces to the central login exactly once,
  // the same "cookie session, redirect if missing" model every other web
  // app in the ecosystem already uses — never a login form of our own.
  const auth = useAuthBootstrap();
  React.useEffect(() => {
    // Inside our own mobile app's WebView, the native app is the ONE sign-in
    // surface (Settings sheet) — bouncing to the website's own cookie login
    // here would show a second, competing login form on top of it. Just wait;
    // the moment the user signs in natively, the token gets pushed in and
    // this same effect re-runs with status already 'ready'.
    if (auth.enabled && auth.status === 'signed-out' && !isHostInjected()) {
      auth.redirectToLogin();
    }
  }, [auth]);

  const [view, setView] = React.useState<View>('choose');
  const [created, setCreated] = React.useState<CreatedRoom | null>(null);

  const today = React.useMemo(() => startOfToday(), []);
  const [selectedDay, setSelectedDay] = React.useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = React.useState<TimeValue | null>(null);
  const [slotError, setSlotError] = React.useState<string | null>(null);
  const [requireAdmission, setRequireAdmission] = React.useState(false);
  const [enableAiCoach, setEnableAiCoach] = React.useState(false);

  const selectedSlot = React.useMemo(
    () => (selectedDay && selectedTime ? combineDateTime(selectedDay, selectedTime) : null),
    [selectedDay, selectedTime],
  );

  const pickDay = React.useCallback((day: Date | undefined) => {
    if (!day) {
      setSelectedDay(null);
      return;
    }
    setSelectedDay(day);
    setSelectedTime((current) => current ?? defaultTimeFor(day));
    setSlotError(null);
  }, []);

  const form = useForm<CreateRoomIdentityValues>({
    resolver: zodResolver(createRoomIdentitySchema),
    defaultValues: { guestName: '' },
    mode: 'onSubmit',
  });

  const resetToChoose = React.useCallback(() => {
    form.reset({ guestName: '' });
    setSelectedDay(null);
    setSelectedTime(null);
    setSlotError(null);
    setRequireAdmission(false);
    setEnableAiCoach(false);
    setView('choose');
  }, [form]);

  const onPickMode = React.useCallback((kind: RoomMeetingKind) => {
    setView(kind === 'instant' ? 'instant' : 'schedule');
  }, []);

  /** Route an ApiError: field errors go INLINE, everything else is a toast. */
  const routeMutationError = React.useCallback(
    (err: unknown) => {
      if (err instanceof ApiError && err.kind === 'validation') {
        const fields = err.fieldErrors;
        let handled = false;
        if (fields.guestName) {
          form.setError('guestName', { message: fields.guestName });
          handled = true;
        }
        if (fields.startTime) {
          setSlotError(fields.startTime);
          handled = true;
        }
        if (handled) return;
      }
      notify.error(err);
    },
    [form],
  );

  // 4h, matching the same instant hard-ceiling the backend computes from
  // meetingType — the guest link's own validity window lines up with it.
  const INSTANT_DURATION_MINUTES = 240;

  const submitInstant = form.handleSubmit(async (values) => {
    try {
      const result = await createRoom.mutateAsync({
        meetingType: 'instant',
        guestName: values.guestName,
        startTime: new Date().toISOString(),
        durationMinutes: INSTANT_DURATION_MINUTES,
        admissionRequired: requireAdmission,
        aiCoachEnabled: enableAiCoach,
      });
      // Don't jump straight into the room — surface the guest link first so the
      // salesperson can actually send it. "Continue to meeting" takes them in.
      setCreated(result);
      setView('created');
    } catch (err) {
      routeMutationError(err);
    }
  });

  const submitScheduled = form.handleSubmit(async (values) => {
    if (!selectedDay || !selectedTime) {
      setSlotError('Pick a day and a time.');
      return;
    }
    const slot = combineDateTime(selectedDay, selectedTime);
    if (slot.getTime() <= Date.now()) {
      setSlotError('Pick a time in the future.');
      return;
    }
    setSlotError(null);
    try {
      const result = await createRoom.mutateAsync({
        meetingType: 'scheduled',
        guestName: values.guestName,
        startTime: slot.toISOString(),
        durationMinutes: SLOT_MINUTES,
        admissionRequired: requireAdmission,
        aiCoachEnabled: enableAiCoach,
      });
      setCreated(result);
      setView('created');
    } catch (err) {
      routeMutationError(err);
    }
  });

  const submitting = createRoom.isPending;
  const onForm = view === 'instant' || view === 'schedule';
  const reduced = usePrefersReducedMotion();

  // While SSO is enabled and we're still resolving (or about to bounce to
  // login), show the same calm loader the join gate uses rather than
  // flashing the create form first. Unreachable whenever auth.enabled is
  // false (the default). Placed after every hook call above so this early
  // return never violates the rules of hooks.
  if (auth.enabled && auth.status !== 'ready') {
    return (
      <BrandingScope branding={branding} className="w-full max-w-4xl">
        <GateLoader
          branding={branding}
          message={
            auth.status === 'signed-out' && isHostInjected()
              ? 'Sign in from Settings to continue…'
              : undefined
          }
        />
      </BrandingScope>
    );
  }

  // Step enter/exit — the SAME treatment as the whiteboard: a plain opacity fade,
  // nothing else. No `layout` height-morph, no transform, no scale-pop (all of
  // which read as "laggy/buggy"). With mode="wait" the card's height change
  // happens while both steps are at opacity 0, so it's never visible — just a
  // clean cross-dissolve, butter-smooth on any device.
  const step = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: reduced ? 0.1 : 0.18, ease: EASE_OUT },
  };

  return (
    <BrandingScope branding={branding} className="w-full max-w-4xl">
      <Card
        variant="elevated"
        padding="none"
        className="w-full overflow-hidden rounded-[var(--radius-lg)] max-sm:!rounded-none max-sm:!border-0 max-sm:!bg-transparent max-sm:!shadow-none max-sm:!overflow-visible"
      >
        {/* Plain container — no `layout` height-morph. The card height just
            follows its content between steps; the light per-step crossfade below
            carries the motion. On phones the card goes chromeless/full-bleed
            (above), so content sits on the page like a native screen. */}
        <div className={onForm ? 'p-0 max-sm:pb-24 sm:p-8 lg:p-10' : 'p-0 sm:p-8 lg:p-10'}>
          {(onForm || view === 'created') && (
            // Desktop keeps Back top-left; on a phone it moves to the bottom
            // thumb bar (beside the primary action) so nothing lives out of
            // reach at the top of the screen.
            <div className="mb-6 hidden sm:block">
              <Button
                variant="ghost"
                onClick={resetToChoose}
                className="-ml-2 text-[var(--text-muted)]"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                Back
              </Button>
            </div>
          )}

          <AnimatePresence mode="wait" initial={false}>
            {view === 'choose' && (
              <motion.div
                key="choose"
                variants={cascadeContainer}
                initial="initial"
                animate="animate"
                exit={{ opacity: 0, transition: { duration: 0.14, ease: EASE_OUT } }}
                className="mx-auto flex w-full max-w-lg flex-col gap-8"
              >
                {/* The two actions are the whole screen — one calm, centred
                      column, nothing competing for attention. */}
                <motion.div variants={cascadeItem} className="space-y-2 text-center">
                  <h1 className="font-heading text-2xl font-semibold tracking-tight text-[var(--text-primary)] sm:text-3xl lg:text-4xl">
                    Start a meeting
                  </h1>
                  <p className="text-[0.95rem] leading-relaxed text-[var(--text-muted)] sm:text-base">
                    Instant or scheduled — either way you get one shareable link.
                  </p>
                </motion.div>

                <ModeCards onSelect={onPickMode} />

                {/* Recents live on their own page now, so this screen stays
                      focused; one quiet link is the way in. */}
                <motion.div variants={cascadeItem} className="flex justify-center">
                  <Button
                    variant="ghost"
                    onClick={() => router.push('/create/recent')}
                    className="text-[var(--text-muted)] hover:text-[var(--primary)]"
                  >
                    <History className="h-4 w-4" aria-hidden="true" />
                    My meetings
                    <ChevronRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </motion.div>
              </motion.div>
            )}

            {view === 'instant' && (
              <motion.form
                key="instant"
                onSubmit={submitInstant}
                initial={step.initial}
                animate={step.animate}
                exit={step.exit}
                transition={step.transition}
                className="grid gap-8 lg:grid-cols-[0.85fr_1fr] lg:gap-10"
                noValidate
              >
                <StepHeader
                  icon={<Video className="h-5 w-5" aria-hidden="true" />}
                  title="Instant meeting"
                  description="Spin up a room right now — you'll join straight in and can share the link from inside."
                >
                  <StepPoints
                    points={[
                      'Room opens instantly',
                      'One shareable link',
                      'Guests join from the link',
                    ]}
                  />
                </StepHeader>

                <motion.div
                  variants={cascadeContainer}
                  initial="initial"
                  animate="animate"
                  className="flex flex-col justify-center gap-6 lg:border-l lg:border-[var(--border)] lg:pl-10"
                >
                  <motion.div variants={cascadeItem}>
                    <Input
                      label="Who are you meeting?"
                      placeholder="e.g. Acme Corp"
                      autoFocus
                      error={form.formState.errors.guestName?.message}
                      {...form.register('guestName')}
                    />
                  </motion.div>
                  <motion.div variants={cascadeItem}>
                    <AdmissionToggle
                      checked={requireAdmission}
                      onCheckedChange={setRequireAdmission}
                    />
                  </motion.div>
                  <motion.div variants={cascadeItem}>
                    <AiCoachToggle
                      checked={enableAiCoach}
                      onCheckedChange={setEnableAiCoach}
                    />
                  </motion.div>
                  <motion.div variants={cascadeItem} className="pt-1 max-sm:hidden">
                    <Button type="submit" isLoading={submitting} className="w-full">
                      Start now
                    </Button>
                  </motion.div>
                </motion.div>

                {/* Phone: Back + primary, both in the thumb zone */}
                <MobileActionBar className="flex items-center gap-2.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={resetToChoose}
                    aria-label="Back"
                    className="shrink-0 px-4"
                  >
                    <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    size="lg"
                    isLoading={submitting}
                    onClick={() => submitInstant()}
                    className="flex-1"
                  >
                    Start now
                  </Button>
                </MobileActionBar>
              </motion.form>
            )}

            {view === 'schedule' && (
              <motion.form
                key="schedule"
                onSubmit={submitScheduled}
                initial={step.initial}
                animate={step.animate}
                exit={step.exit}
                transition={step.transition}
                className="grid gap-8 lg:grid-cols-[0.85fr_1fr] lg:gap-10"
                noValidate
              >
                <StepHeader
                  icon={<CalendarClock className="h-5 w-5" aria-hidden="true" />}
                  title="Schedule meeting"
                  description="Pick a day and time — no calendar app needed. We'll generate the shareable link for you."
                >
                  <div className="rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface-hover)] px-4 py-3">
                    <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-[var(--text-muted)]">
                      When
                    </p>
                    <p className="mt-0.5 font-heading text-base font-semibold text-[var(--text-primary)]">
                      {selectedSlot ? meetingStamp(selectedSlot) : 'Pick a day & time'}
                    </p>
                  </div>
                </StepHeader>

                <motion.div
                  variants={cascadeContainer}
                  initial="initial"
                  animate="animate"
                  className="flex flex-col justify-center gap-6 lg:border-l lg:border-[var(--border)] lg:pl-10"
                >
                  <motion.div variants={cascadeItem}>
                    <Input
                      label="Who are you meeting?"
                      placeholder="e.g. Northwind Traders"
                      autoFocus
                      error={form.formState.errors.guestName?.message}
                      {...form.register('guestName')}
                    />
                  </motion.div>

                  <motion.div
                    variants={cascadeItem}
                    className="grid grid-cols-1 gap-4 sm:grid-cols-2"
                  >
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-[var(--foreground)]">Day</p>
                      <DatePicker
                        value={selectedDay ?? undefined}
                        onChange={pickDay}
                        min={today}
                        placeholder="Pick a day"
                        clearable={false}
                        error={Boolean(slotError) && !selectedDay}
                        className="w-full"
                      />
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-[var(--foreground)]">Time</p>
                      <TimePicker
                        value={selectedTime ?? undefined}
                        onChange={(t) => {
                          setSelectedTime(t ?? null);
                          setSlotError(null);
                        }}
                        use12Hour
                        minuteStep={15}
                        placeholder="Pick a time"
                        isDisabled={!selectedDay}
                        error={Boolean(slotError) && Boolean(selectedDay) && !selectedTime}
                        className="w-full"
                      />
                    </div>
                  </motion.div>

                  {slotError && (
                    <p role="alert" className="text-sm font-medium text-[var(--destructive)]">
                      {slotError}
                    </p>
                  )}

                  <motion.div variants={cascadeItem}>
                    <AdmissionToggle
                      checked={requireAdmission}
                      onCheckedChange={setRequireAdmission}
                    />
                  </motion.div>
                  <motion.div variants={cascadeItem}>
                    <AiCoachToggle
                      checked={enableAiCoach}
                      onCheckedChange={setEnableAiCoach}
                    />
                  </motion.div>

                  <motion.div variants={cascadeItem} className="pt-1 max-sm:hidden">
                    <Button
                      type="submit"
                      isLoading={submitting}
                      disabled={!selectedSlot}
                      className="w-full"
                    >
                      Schedule meeting
                    </Button>
                  </motion.div>
                </motion.div>

                {/* Phone: Back + primary, both in the thumb zone */}
                <MobileActionBar className="flex items-center gap-2.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={resetToChoose}
                    aria-label="Back"
                    className="shrink-0 px-4"
                  >
                    <ArrowLeft className="h-5 w-5" aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    size="lg"
                    isLoading={submitting}
                    disabled={!selectedSlot}
                    onClick={() => submitScheduled()}
                    className="flex-1"
                  >
                    Schedule meeting
                  </Button>
                </MobileActionBar>
              </motion.form>
            )}

            {view === 'created' && created && (
              <motion.div
                key="created"
                initial={step.initial}
                animate={step.animate}
                exit={step.exit}
                transition={step.transition}
                className="mx-auto w-full max-w-xl"
              >
                <CreatedResult created={created} onCreateAnother={resetToChoose} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Card>
    </BrandingScope>
  );
}
